import { useState, useEffect } from 'react'
import { useAppSelector } from '@/store/hooks'
import { selectFigmaToken } from '@/store/workspace/workspaceSelectors'
import { parseFigmaUrl } from '../../TextEditor/plugins/figmaPlugin'
import { getFigmaCache, setFigmaCache } from '../../TextEditor/components/TextEditorFigma/figmaCache'
import FigmaLightbox from '../../TextEditor/components/TextEditorFigma/components/FigmaLightbox/FigmaLightbox'
import FigmaIcon from '@/assets/icons/icon-figma.svg?react'
import css from '../../TextEditor/components/TextEditorFigma/TextEditorFigma.module.css'

// Read-only Figma preview for the static viewer. Same fetch/cache + lightbox as the
// editor's TextEditorFigma, minus Slate selection and the editing-only "convert to link" action.
const StaticFigma = ({ attributes, children, element }: any) => {
    const figmaToken = useAppSelector(selectFigmaToken)

    const [imageUrl, setImageUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [errorKind, setErrorKind] = useState<'auth' | 'fetch' | 'no_node' | null>(null)
    const [frameName, setFrameName] = useState<string | null>(null)
    const [fileName, setFileName] = useState<string | null>(null)
    const [lastModified, setLastModified] = useState<string | null>(null)
    const [showLightbox, setShowLightbox] = useState(false)

    useEffect(() => {
        if (!figmaToken || !element.url) {
            setLoading(false)
            return
        }

        const parsed = parseFigmaUrl(element.url)
        if (!parsed?.nodeId) {
            setLoading(false)
            setErrorKind('no_node')
            return
        }

        const cached = getFigmaCache(element.url)
        if (cached) {
            setImageUrl(cached.imageUrl)
            setFrameName(cached.frameName)
            setFileName(cached.fileName)
            setLastModified(cached.lastModified)
            return
        }

        setLoading(true)
        setErrorKind(null)

        const controller = new AbortController()
        const { fileKey, nodeId } = parsed
        const ids = encodeURIComponent(nodeId)
        const headers = { 'Authorization': `Bearer ${figmaToken}` }

        Promise.all([
            fetch(`https://api.figma.com/v1/images/${fileKey}?ids=${ids}&format=png&scale=2`, { headers, signal: controller.signal })
                .then(r => {
                    if (r.status === 401 || r.status === 403) { setErrorKind('auth'); return null }
                    return r.json()
                }),
            fetch(`https://api.figma.com/v1/files/${fileKey}/nodes?ids=${ids}`, { headers, signal: controller.signal })
                .then(r => r.ok ? r.json() : null),
        ])
            .then(([imageData, nodeData]) => {
                const imageUrl = imageData?.images?.[nodeId] ?? null
                if (!imageData) return
                if (!imageUrl) { setErrorKind('fetch'); return }

                const frameName = nodeData?.nodes?.[nodeId]?.document?.name ?? null
                const fileName = nodeData?.name ?? null
                const lastModified = nodeData?.lastModified ?? null

                setImageUrl(imageUrl)
                setFrameName(frameName)
                setFileName(fileName)
                setLastModified(lastModified)
                setFigmaCache(element.url, { imageUrl, frameName, fileName, lastModified })
            })
            .catch(err => {
                if (err?.name === 'AbortError') return
                setErrorKind('fetch')
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false)
            })

        return () => controller.abort()
    }, [figmaToken, element.url])

    const footerLabel = frameName || fileName || 'Figma'

    return (
        <div {...attributes}>
            <div contentEditable={false} className={css.wrapper}>
                {loading && <div className={css.skeleton} />}
                {!loading && imageUrl && (
                    <img
                        src={imageUrl}
                        alt="Figma frame"
                        className={css.image}
                        data-lightbox-trigger
                        onClick={() => setShowLightbox(true)}
                    />
                )}
                {!loading && !imageUrl && (
                    <div className={css.placeholder}>
                        <FigmaIcon className={css.placeholder_icon} />
                        <span className={css.placeholder_text}>
                            {!figmaToken
                                ? 'Connect Figma in workspace settings to see preview'
                                : errorKind === 'auth'
                                    ? 'Session expired — workspace owner needs to reconnect Figma in settings'
                                    : errorKind === 'no_node'
                                        ? 'Add ?node-id= to the URL to load a frame preview'
                                        : errorKind === 'fetch'
                                            ? 'Unable to load preview'
                                            : null}
                        </span>
                    </div>
                )}
                <div className={css.footer}>
                    <FigmaIcon className={css.footer_icon} />
                    <span className={css.footer_name}>{footerLabel}</span>
                </div>
            </div>
            {showLightbox && imageUrl && (
                <FigmaLightbox
                    imageUrl={imageUrl}
                    frameUrl={element.url}
                    frameName={frameName}
                    fileName={fileName}
                    lastModified={lastModified}
                    onClose={() => setShowLightbox(false)}
                />
            )}
            {children}
        </div>
    )
}

export default StaticFigma
